// Runs a ticket action so the SCREEN moves first and the server confirms later.
//
// Every action still goes through the n8n dispatcher (CLAUDE.md §5/§6): this
// file never writes to the database. What it does is:
//
//   1. PREPARE  — predict what the dispatcher is about to write
//      (ticket-optimistic.ts) and put that into every cached copy of the
//      ticket (detail page, dashboard + My Day lists, search results, contact
//      lists). Synchronous, so the click shows its result immediately.
//   2. COMMIT   — send the action. Actions on the SAME ticket are sent one
//      after another, so a quick claim-then-resolve can't race on the server.
//   3. SETTLE   — success: the server's row replaces the prediction (unless a
//      later action on the same ticket is still queued). Failure: put back
//      only the fields that still hold the predicted value, then rethrow so
//      the caller can say so.
//
// While an action is in flight, any ticket data that arrives from the server
// (a list refetch started before the server finished) has the still-pending
// predictions re-applied, so the screen can't flash back to the old state.
//
// No Supabase / React imports on purpose: tests/ticket-actions.test.ts drives
// it with a plain QueryClient and a fake `send`.

import type { QueryClient } from '@tanstack/react-query';
import {
  optimisticTicketPatch,
  revertTicketPatch,
  type PatchableTicket,
  type TicketFieldPatch,
} from './ticket-optimistic';

export interface ActionIntent {
  action: string;
  ticket_id: string;
  payload?: Record<string, unknown>;
}

export interface TicketActionRequest {
  ticketId: string;
  action: string;
  payload?: Record<string, unknown>;
}

export interface ActionContext {
  /** The signed-in agent's slack_user_id (what `claim` assigns to). */
  actorSlackId: string | null;
  /** Posts the intent to the dispatcher. Rejects on any failure, including a
   *  refusal the server returns inside a 200 (see postAction). */
  send: (intent: ActionIntent) => Promise<unknown>;
  /** Called once NOTHING is in flight any more: refetch lists and counts from
   *  the server. Deferred so rapid triage doesn't refetch (and flicker) between
   *  clicks. */
  refreshLists: () => void;
  /** Test hook. */
  now?: () => Date;
}

export interface PreparedAction {
  /** True when the screen already changed. False when nothing could be
   *  predicted (ticket not loaded, or the action changes no ticket field). */
  patched: boolean;
  /** Send it. Resolves with the server's reply; rejects (after the screen has
   *  been put back) if the server refuses or can't be reached. Calling it more
   *  than once returns the same promise. */
  commit: () => Promise<unknown>;
}

/** Every query key whose cache holds ticket rows we know how to patch. */
export const TICKET_ROW_KEYS = [
  'ticket',
  'tickets',
  'ticket-search-v2',
  'ticket-search-palette',
  'tickets-recent',
  'contact-tickets',
  'contact-recent-tickets',
  'contact-tickets-page',
  'my-watched-tickets',
] as const;
const ROW_KEY_SET: ReadonlySet<string> = new Set(TICKET_ROW_KEYS);

/**
 * The dispatcher refuses an action with HTTP 200 and `{ ok: false, error,
 * reason }` (it can't set a status from inside the workflow), so a plain
 * "response was 2xx" check reports a refused action as done. Call this on the
 * parsed reply; it throws for a refusal and returns the reply otherwise
 * (including `undefined` for an empty / non-JSON body).
 */
export function assertActionAccepted<T>(reply: T): T {
  if (reply && typeof reply === 'object' && (reply as { ok?: unknown }).ok === false) {
    const { error, reason } = reply as { error?: unknown; reason?: unknown };
    const what = typeof error === 'string' && error ? error : 'action_refused';
    throw new Error(typeof reason === 'string' && reason ? `${what} (${reason})` : what);
  }
  return reply;
}

// ─── Cache rewriting ─────────────────────────────────────────────────────────

type Row = Record<string, unknown>;

/**
 * Rewrites every ticket row with this id inside any of the cached shapes
 * (a row, an array of rows, `{ rows }`, `{ rows: [{ ticket }] }`, infinite
 * `{ pages }`). Returns `data` itself when nothing changed, so React doesn't
 * re-render for no reason.
 */
function mapTicketData(data: unknown, ticketId: string, fn: (row: Row) => Row): unknown {
  if (Array.isArray(data)) {
    let changed = false;
    const out = data.map((item) => {
      const next = mapTicketData(item, ticketId, fn);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : data;
  }
  if (!data || typeof data !== 'object') return data;
  const obj = data as Row;
  if (typeof obj.id === 'string') return obj.id === ticketId ? fn(obj) : data;
  let out: Row | null = null;
  for (const k of ['ticket', 'rows', 'pages']) {
    if (!(k in obj)) continue;
    const next = mapTicketData(obj[k], ticketId, fn);
    if (next !== obj[k]) {
      out ??= { ...obj };
      out[k] = next;
    }
  }
  return out ?? data;
}

/** Sets fields on a row — only fields the row already has, so a list that
 *  selects a few columns doesn't grow ones it never loaded. */
function withFields(row: Row, fields: Row): Row {
  let next: Row | null = null;
  for (const [k, v] of Object.entries(fields)) {
    if (k in row && row[k] !== v) {
      next ??= { ...row };
      next[k] = v;
    }
  }
  return next ?? row;
}

function writeFields(qc: QueryClient, ticketId: string, fields: Row) {
  for (const key of TICKET_ROW_KEYS) {
    qc.setQueriesData({ queryKey: [key] }, (old: unknown) =>
      old === undefined ? old : mapTicketData(old, ticketId, (row) => withFields(row, fields)),
    );
  }
}

/** Change fields on every cached copy of a ticket (lists, search results, the open
 *  ticket) at once. Only fields a copy already has are set. Used for personal state
 *  that is not a dispatcher action, e.g. the Unread flag. */
export function patchTicketFields(qc: QueryClient, ticketId: string, fields: Record<string, unknown>) {
  writeFields(qc, ticketId, fields);
}

function writeRevert(qc: QueryClient, ticketId: string, applied: TicketFieldPatch, before: PatchableTicket) {
  for (const key of TICKET_ROW_KEYS) {
    qc.setQueriesData({ queryKey: [key] }, (old: unknown) =>
      old === undefined
        ? old
        : mapTicketData(old, ticketId, (row) =>
            withFields(row, revertTicketPatch(row as PatchableTicket, applied, before) as Row),
          ),
    );
  }
}

/** A FULL cached copy of the ticket (the one the action is predicted from). */
function findCachedTicket(qc: QueryClient, ticketId: string): PatchableTicket | null {
  let found: PatchableTicket | null = null;
  const visit = (data: unknown) => {
    if (found || data === undefined) return;
    mapTicketData(data, ticketId, (row) => {
      // A full `select('*')` row has an assignee column; a trimmed list doesn't.
      if (!found && 'assignee_slack_id' in row && 'status' in row) found = row as PatchableTicket;
      return row;
    });
  };
  visit(qc.getQueryData(['ticket', ticketId]));
  for (const key of TICKET_ROW_KEYS) {
    if (found) break;
    for (const [, data] of qc.getQueriesData({ queryKey: [key] })) visit(data);
  }
  return found;
}

// ─── Per-client bookkeeping ──────────────────────────────────────────────────

interface Pending {
  seq: number;
  fields: TicketFieldPatch;
}

interface Registry {
  seq: number;
  /** Predictions applied to the screen that the server hasn't confirmed yet. */
  pending: Map<string, Pending[]>;
  /** Prepared actions not yet settled, across every ticket. */
  inflight: number;
  /** Tail of each ticket's send queue. */
  chains: Map<string, Promise<unknown>>;
  /** Latest action sequence prepared for each ticket. */
  latest: Map<string, number>;
}

const registries = new WeakMap<QueryClient, Registry>();

function registryFor(qc: QueryClient): Registry {
  let reg = registries.get(qc);
  if (reg) return reg;
  reg = { seq: 0, pending: new Map(), inflight: 0, chains: new Map(), latest: new Map() };
  registries.set(qc, reg);
  watchArrivingData(qc, reg);
  return reg;
}

/** Ticket data fetched from the server while an action is still running can't
 *  know about it yet; lay the pending predictions back over it. */
function watchArrivingData(qc: QueryClient, reg: Registry) {
  qc.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || reg.pending.size === 0) return;
    const action = event.action as { type?: string; manual?: boolean };
    // `manual` = a setQueryData (ours included): not data from the server.
    if (action.type !== 'success' || action.manual) return;
    const key = event.query.queryKey;
    if (!ROW_KEY_SET.has(String(key[0]))) return;
    const data = event.query.state.data;
    let next = data;
    for (const [ticketId, list] of reg.pending) {
      const merged = Object.assign({}, ...list.map((p) => p.fields)) as Row;
      next = mapTicketData(next, ticketId, (row) => withFields(row, merged));
    }
    if (next !== data) qc.setQueryData(key, next);
  });
}

/** Runs `job` after everything already queued for this ticket, whether those
 *  succeeded or not. With nothing queued it starts straight away. */
function enqueue<T>(reg: Registry, ticketId: string, job: () => Promise<T>): Promise<T> {
  const prev = reg.chains.get(ticketId);
  let run: Promise<T>;
  if (prev) {
    run = prev.then(job, job);
  } else {
    try {
      run = job();
    } catch (err) {
      run = Promise.reject(err);
    }
  }
  const tail = run.then(
    () => undefined,
    () => undefined,
  );
  reg.chains.set(ticketId, tail);
  void tail.then(() => {
    if (reg.chains.get(ticketId) === tail) reg.chains.delete(ticketId);
  });
  return run;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/** Whether any prepared action hasn't been confirmed yet (used to warn before
 *  the tab is closed). */
export function hasPendingTicketActions(qc: QueryClient): boolean {
  return (registries.get(qc)?.inflight ?? 0) > 0;
}

/** The ticket ids that still have an unconfirmed action (tests, diagnostics). */
export function pendingTicketIds(qc: QueryClient): string[] {
  return [...(registries.get(qc)?.pending.keys() ?? [])];
}

/**
 * Step 1 — update the screen now. Always follow with `commit()`: an action
 * that is prepared but never committed keeps list refreshes on hold.
 */
export function prepareTicketAction(
  qc: QueryClient,
  req: TicketActionRequest,
  ctx: ActionContext,
): PreparedAction {
  const reg = registryFor(qc);
  const { ticketId } = req;
  const seq = ++reg.seq;

  const before = findCachedTicket(qc, ticketId);
  const fields = before
    ? optimisticTicketPatch(before, req.action, req.payload, {
        actorSlackId: ctx.actorSlackId,
        now: ctx.now?.(),
      })
    : null;

  reg.inflight++;
  reg.latest.set(ticketId, seq);
  if (fields) {
    const list = reg.pending.get(ticketId) ?? [];
    list.push({ seq, fields });
    reg.pending.set(ticketId, list);
    writeFields(qc, ticketId, fields as Row);
  }

  const settle = (reply: unknown, failed: boolean) => {
    // Forget the prediction before anything refetches, or the arriving-data
    // handler would re-apply it over the server's answer.
    const rest = (reg.pending.get(ticketId) ?? []).filter((p) => p.seq !== seq);
    if (rest.length > 0) reg.pending.set(ticketId, rest);
    else reg.pending.delete(ticketId);

    const isLatest = reg.latest.get(ticketId) === seq;
    if (isLatest) reg.latest.delete(ticketId);

    if (failed) {
      if (fields && before) writeRevert(qc, ticketId, fields, before);
    } else if (isLatest) {
      // The server's own row is the truth — but only when no later action on
      // this ticket is still waiting, whose prediction it would overwrite.
      const updated = (reply as { updated?: unknown } | undefined)?.updated;
      if (updated && typeof updated === 'object' && (updated as Row).id === ticketId) {
        writeFields(qc, ticketId, updated as Row);
      }
    }

    qc.invalidateQueries({ queryKey: ['ticket-events', ticketId] });
    qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
    const mergeTarget = req.action === 'merge' ? req.payload?.merge_into_ticket_id : undefined;
    if (typeof mergeTarget === 'string' && mergeTarget) {
      qc.invalidateQueries({ queryKey: ['ticket', mergeTarget] });
      qc.invalidateQueries({ queryKey: ['ticket-events', mergeTarget] });
      qc.invalidateQueries({ queryKey: ['ticket-messages', mergeTarget] });
    }

    reg.inflight--;
    if (reg.inflight === 0) ctx.refreshLists();
  };

  let sent: Promise<unknown> | null = null;
  const commit = () => {
    sent ??= enqueue(reg, ticketId, () =>
      ctx.send({ action: req.action, ticket_id: ticketId, payload: req.payload }),
    ).then(
      (reply) => {
        settle(reply, false);
        return reply;
      },
      (err: unknown) => {
        settle(undefined, true);
        throw err;
      },
    );
    return sent;
  };

  return { patched: fields !== null, commit };
}
